import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
    ATTACHMENT_LEVEL,
    ATTACHMENT_STATUS,
    FILE_NAME_REGEX,
    MAX_ATTACHMENT_DESCRIPTION_LENGTH,
    MAX_ATTACHMENT_FILE_SIZE,
    MAX_ATTACHMENT_FILENAME_LENGTH,
    MAX_ATTACHMENT_TITLE_LENGTH,
    MAX_UPLOADED_ATTACHMENTS_PER_LINE,
    MIN_ATTACHMENT_FILE_SIZE,
} from "constants/generalConstants";
import useAttachments from "hooks/useAttachments";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import alertDispatcher from "utils/alertsUtils";

import * as constants from "../../../../constants/snackbarMessage";
import { setLevelType } from "../../../../redux/actions/attachmentActions";
import { uploadAnAttachmentApi } from "../../../../services/attachment/attachmentAPIs";
import { freeTextFormProps } from "../../../../styled/props/textFieldProps";
import AttachmentInfoPopper from "./AttachmentInfoPopper";
import UploadedFiles from "./UploadedFiles";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

export default function Upload({ openUploadDialog, handleClose }) {
    const [fileTitle, setFileTitle] = useState("");
    const [fileType, setFileType] = useState("");

    const [description, setDescription] = useState("");
    const { headerAttachments, getAttachmentsByUniqueLineId } =
        useAttachments();
    const [typeError, setTypeError] = useState(false);
    const fileInputRef = useRef(null);

    const allowedTypes = {
        pdf: ".pdf",
        word: ".docx",
        excel: ".xlsx",
        email: ".msg",
    };
    const validExtensions = ["pdf", "docx", "xlsx", "msg"];

    const dispatch = useDispatch();

    const levelType = useSelector((state) => state.attachment.levelType);
    const currentLineIndex = useSelector(
        (state) => state.attachment.attachmentLineItem.lineIndex
    );
    const currentLineUniqueLineId = useSelector(
        (state) => state.attachment.attachmentLineItem.uniqueLineId
    );

    const isPostPoReq = useSelector(
        (state) => state.createRequisition.isPostPoReq
    );

    let filteredAttachments = [];
    if (levelType === ATTACHMENT_LEVEL.HEADER) {
        filteredAttachments = headerAttachments;
    } else {
        filteredAttachments = getAttachmentsByUniqueLineId(
            currentLineUniqueLineId
        );
    }

    const scansStillPending = filteredAttachments.some(
        (attachment) => attachment.status === ATTACHMENT_STATUS.SCAN_PENDING
    );

    const maxAttachmentsReached =
        filteredAttachments.length == MAX_UPLOADED_ATTACHMENTS_PER_LINE;

    const checkFileSize = (file) => {
        if (file.size < MIN_ATTACHMENT_FILE_SIZE) {
            alertDispatcher(
                "validateAttachments",
                400,
                dispatch,
                constants.MIN_FILE_SIZE_CHECK
            );
            return false;
        }
        if (file.size > MAX_ATTACHMENT_FILE_SIZE) {
            alertDispatcher(
                "validateAttachments",
                400,
                dispatch,
                constants.MAX_FILE_SIZE_CHECK
            );
            return false;
        }
        return true;
    };

    const checkFileNaming = (file) => {
        if (!FILE_NAME_REGEX.test(file.name)) {
            alertDispatcher(
                "validateAttachments",
                400,
                dispatch,
                constants.FILENAME_SPECIAL_CHARACTERS
            );
            return false;
        }
        return true;
    };

    const checkFileNameLength = (file) => {
        if (file.name.length > MAX_ATTACHMENT_FILENAME_LENGTH) {
            alertDispatcher(
                "validateAttachments",
                400,
                dispatch,
                constants.ATTACHMENT_NAME_MAXLENGTH
            );
            return false;
        }
        return true;
    };

    const checkValidFileType = (file) => {
        const fileExtension = file.name.split(".").pop().toLowerCase();
        if (!validExtensions.includes(fileExtension)) {
            alertDispatcher(
                "validateAttachments",
                400,
                dispatch,
                constants.FILE_FORMAT_ERROR
            );
            return false;
        }
        return true;
    };

    const getAndResetFileTitle = (file) => {
        // If the user hasn't provided a title, the file name will be used as the title instead.
        let fileTitleOverAll = "";
        if (fileTitle) {
            fileTitleOverAll = fileTitle;
        } else {
            fileTitleOverAll =
                file.name.length > MAX_ATTACHMENT_TITLE_LENGTH
                    ? file.name.slice(0, MAX_ATTACHMENT_TITLE_LENGTH)
                    : file.name;
        }
        setFileTitle("");
        return fileTitleOverAll;
    };

    const getAndResetDescription = () => {
        let descriptionOverAll = "";
        if (description) {
            descriptionOverAll = description;
        }
        setDescription("");
        return descriptionOverAll;
    };

    const sanitizeText = (input, field) => {
        const validationMessage =
            field === "title"
                ? constants.FILETITLE_SPECIAL_CHARACTERS
                : constants.FILE_DESCRIPTION_SPECIAL_CHARACTERS;
        const fileTitleRegex = /[^a-zA-Z0-9-_ .]/g; // Matches any character that is not alphanumeric, hyphen, underscore, space, or period

        if (fileTitleRegex.test(input)) {
            alertDispatcher(
                "validateAttachments",
                400,
                dispatch,
                validationMessage
            );
        }
        return input.replace(fileTitleRegex, "");
    };

    const handleOnClick = () => {
        if (!levelType) {
            setTypeError(true);
            return;
        }
        setTypeError(false);
        fileInputRef.current.click();
    };

    const handleFileInput = (event) => {
        const file = event.target.files[0];
        fileInputRef.current.value = ""; //clearing the ref to clear the cached file and to show the error messages
        if (!file) return;
        if (!checkFileSize(file)) {
            return;
        }
        if (!checkFileNaming(file)) {
            return;
        }
        if (!checkFileNameLength(file)) {
            return;
        }
        if (!checkValidFileType(file)) {
            return;
        }
        let fileTitleOverAll = getAndResetFileTitle(file);
        let descriptionOverAll = getAndResetDescription();

        try {
            const requestBody = new FormData();
            requestBody.append("file", file);
            event.target.value = "";
            return uploadAnAttachmentApi(
                requestBody,
                levelType,
                currentLineIndex,
                currentLineUniqueLineId,
                fileTitleOverAll,
                descriptionOverAll,
                file.size,
                file.name,
                dispatch
            );
        } catch (error) {
            console.log(error);
        }
    };

    const handleProceed = () => {
        cleanUpAndClose();
    };

    const cleanUpAndClose = () => {
        setFileTitle("");
        setDescription("");
        setFileType("");
        handleClose();
    };

    let toolTipUploadFiles = "Upload file";
    if (isPostPoReq && levelType === ATTACHMENT_LEVEL.HEADER) {
        toolTipUploadFiles =
            "Files cannot be attached to header after a Purchase Order has been created.";
    } else if (maxAttachmentsReached) {
        toolTipUploadFiles =
            "Maximum attachment limit reached, you can attach up to 15 files per line or header.";
    }

    return (
        <>
            <Dialog
                open={openUploadDialog}
                onClick={(event) => event.stopPropagation()}
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Box>Add Attachment to Header and Line</Box>
                        <AttachmentInfoPopper />
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2}>
                        <TextField
                            {...freeTextFormProps}
                            label="Attachment title"
                            variant="standard"
                            name="title"
                            value={fileTitle}
                            inputProps={{
                                maxLength: MAX_ATTACHMENT_TITLE_LENGTH,
                            }}
                            onChange={(event) =>
                                setFileTitle(
                                    sanitizeText(event.target.value, "title")
                                )
                            }
                            multiline
                            fullWidth
                        />
                        <TextField
                            {...freeTextFormProps}
                            label="Description"
                            variant="standard"
                            name="description"
                            value={description}
                            inputProps={{
                                maxLength: MAX_ATTACHMENT_DESCRIPTION_LENGTH,
                            }}
                            onChange={(event) =>
                                setDescription(
                                    sanitizeText(
                                        event.target.value,
                                        "description"
                                    )
                                )
                            }
                            multiline
                            fullWidth
                        />

                        <Select
                            label="Attachment Type"
                            variant="standard"
                            name="type"
                            value={levelType}
                            onChange={(event) => {
                                dispatch(setLevelType(event.target.value));
                                setTypeError(false);
                            }}
                            error={typeError}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) {
                                    return (
                                        <span style={{ color: "#aaa" }}>
                                            Click dropdown to select header or
                                            line attachment
                                        </span>
                                    );
                                }
                                return selected === "HEADER"
                                    ? "Header Attachment"
                                    : "Line Item Attachment";
                            }}
                        >
                            <MenuItem value={ATTACHMENT_LEVEL.HEADER}>
                                Header Attachment
                            </MenuItem>
                            <MenuItem value={ATTACHMENT_LEVEL.LINE}>
                                Line Item Attachment
                            </MenuItem>
                        </Select>
                        {typeError && (
                            <Box
                                sx={{
                                    color: "error.main",
                                    fontSize: "0.75rem",
                                    mt: -1,
                                }}
                            >
                                Attachment Type is required
                            </Box>
                        )}

                        <Stack direction="row" justifyContent="end">
                            <Tooltip title={toolTipUploadFiles}>
                                <span>
                                    <LoadingButton
                                        disabled={
                                            (levelType ===
                                                ATTACHMENT_LEVEL.HEADER &&
                                                isPostPoReq) ||
                                            maxAttachmentsReached
                                        }
                                        component="label"
                                        variant="contained"
                                        tabIndex={-1}
                                        onClick={handleOnClick}
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        Upload file
                                    </LoadingButton>
                                    <VisuallyHiddenInput
                                        type="file"
                                        ref={fileInputRef}
                                        name="file"
                                        onInput={(event) =>
                                            handleFileInput(event)
                                        }
                                        accept={
                                            fileType
                                                ? allowedTypes[fileType]
                                                : ""
                                        }
                                    />
                                </span>
                            </Tooltip>
                        </Stack>
                        <UploadedFiles
                            titleText="Upload Attachments"
                            mode="Upload"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={cleanUpAndClose}
                        color="error"
                        disabled={scansStillPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleProceed}
                        type="submit"
                        disabled={scansStillPending}
                    >
                        Proceed
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
